import { HTMLAttributes, isValidElement, ReactElement, useId } from "react";
import { useControlledValue } from "../../hooks";
import { cn, Slot } from "../../utils";
import { Dropdown } from "../Dropdown";
import { PopoverProps } from "./types";

export const Popover = (props: PopoverProps) => {
  const {
    trigger,
    children,
    content,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    defaultIsOpen,
    placement = "bottom-start",
    offset = 8,
    maxWidth = 320,
    contentClassName,
    // Dropdown-specific props
    flip,
    shift,
    autoPlacement,
    middleware,
    clickOptions,
    hoverOptions,
    focusOptions,
    dismissOptions,
    roleOptions,
    listNavigationOptions,
    typeaheadOptions,
    floatingFocusManager: floatingFocusManagerProp,
    ariaLabel,
    triggerId: propsTriggerId,
    contentId: propsContentId,
    minWidth,
    minHeight,
    maxHeight,
    matchTriggerWidth,
    typeaheadListRef,
    disabled,
    preventDefaultOnTrigger,
    stopPropagationOnTrigger,
    portal,
    // Forward remaining props (from parent wrappers like Tooltip) to trigger
    ...forwardedProps
  } = props;

  const { value: open, onChange: onOpenChange } = useControlledValue<boolean>({
    controlledValue: controlledOpen,
    controlledOnChange: controlledOnOpenChange,
    defaultValue: defaultIsOpen,
  });

  const generatedId = useId();
  const triggerId =
    propsTriggerId || children.props.id || `${generatedId}-trigger`;
  const contentId =
    propsContentId ||
    (isValidElement(content) &&
    (content as ReactElement<HTMLAttributes<HTMLElement>>).props.id
      ? (content as ReactElement<HTMLAttributes<HTMLElement>>).props.id
      : `${generatedId}-content`);

  const triggerElementBase = trigger || children;

  const triggerElement = <Slot {...forwardedProps}>{triggerElementBase}</Slot>;

  return (
    <Dropdown
      trigger={triggerElement}
      content={content}
      triggerId={triggerId}
      contentId={contentId}
      open={open}
      onOpenChange={onOpenChange}
      interactions={["click", "dismiss"]}
      role="dialog"
      placement={placement}
      offset={offset}
      maxWidth={maxWidth}
      floatingFocusManager={{
        initialFocus: 0,
        returnFocus: true,
        restoreFocus: true,
        modal: false,
        ...floatingFocusManagerProp,
      }}
      contentClassName={cn("px-3 py-2", contentClassName)}
      flip={flip}
      shift={shift}
      autoPlacement={autoPlacement}
      middleware={middleware}
      clickOptions={clickOptions}
      hoverOptions={hoverOptions}
      focusOptions={focusOptions}
      dismissOptions={dismissOptions}
      roleOptions={roleOptions}
      listNavigationOptions={listNavigationOptions}
      typeaheadOptions={typeaheadOptions}
      ariaLabel={ariaLabel}
      minWidth={minWidth}
      minHeight={minHeight}
      maxHeight={maxHeight}
      matchTriggerWidth={matchTriggerWidth}
      typeaheadListRef={typeaheadListRef}
      disabled={disabled}
      preventDefaultOnTrigger={preventDefaultOnTrigger}
      stopPropagationOnTrigger={stopPropagationOnTrigger}
      portal={portal}
    />
  );
};
